import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_ENDPOINTS } from "../config/constants";
import api from "../utils/api";
import { getReadableApiError } from "../utils/readableApiResponse";
import { logout } from "./authSlice";

const getInitialProductsState = () => ({
    products: [],
    selectedProduct: null,

    productsStatus: "idle",
    productsError: null,

    productDetailsStatus: "idle",
    productDetailsError: null,

    createProductStatus: "idle",
    createProductError: null,

    updateProductStatus: "idle",
    updateProductError: null,

    productSequenceCountById: {},
    productSequenceCountStatusById: {},
    productSequenceCountErrorById: {},

    productCampaignsByProductId: {},
    productCampaignsStatusByProductId: {},
    productCampaignsErrorByProductId: {},
});

const getTrimmedString = (value) =>
    typeof value === "string" ? value.trim() : "";

const getProductId = (value) => {
    if (typeof value === "string" || typeof value === "number") {
        return String(value).trim();
    }

    return (
        getTrimmedString(value?.productId) ||
        getTrimmedString(value?.product_id) ||
        getTrimmedString(value?.id)
    );
};

const getSafeArray = (value) => (Array.isArray(value) ? value : []);

const hasOwn = (object, key) =>
    Object.prototype.hasOwnProperty.call(object, key);

const buildQueryString = (params = {}) => {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;

        if (Array.isArray(value)) {
            value.forEach((item) => {
                if (item !== undefined && item !== null && item !== "") {
                    searchParams.append(key, item);
                }
            });
            return;
        }

        searchParams.append(key, value);
    });

    return searchParams.toString();
};

const getProductCampaignRequest = (request = {}) => {
    if (typeof request === "string" || typeof request === "number") {
        return {
            productId: String(request).trim(),
            queryParams: {},
        };
    }

    const {
        productId,
        product_id: productIdSnake,
        id,
        params,
        queryParams,
        ...restParams
    } = request || {};

    return {
        productId:
            getTrimmedString(productId) ||
            getTrimmedString(productIdSnake) ||
            getTrimmedString(id),
        queryParams: {
            ...(params || {}),
            ...(queryParams || {}),
            ...restParams,
        },
    };
};

// Gets all products list
export const fetchProducts = createAsyncThunk(
    "products/fetchProducts",
    async (_, thunkAPI) => {
        try {
            const response = await api.get(API_ENDPOINTS.OUTREACH_PRODUCTS);

            return getSafeArray(response.data);
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(error, "Failed to fetch products."),
            );
        }
    },
);

// Get details of a selected product
export const fetchProductById = createAsyncThunk(
    "products/fetchProductById",
    async (productId, thunkAPI) => {
        const normalizedProductId = getProductId(productId);

        if (!normalizedProductId) {
            return thunkAPI.rejectWithValue("Invalid product id.");
        }

        try {
            const response = await api.get(
                API_ENDPOINTS.OUTREACH_PRODUCT_BY_ID(normalizedProductId),
            );

            return response.data || null;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(error, "Failed to fetch product details."),
            );
        }
    },
);

// Creates new product
export const createProduct = createAsyncThunk(
    "products/createProduct",
    async (productData = {}, thunkAPI) => {
        const organizationId = getTrimmedString(productData.organizationId);
        const requestBody = {
            name: getTrimmedString(productData.name),
            description: getTrimmedString(productData.description),
        };

        if (!organizationId) {
            return thunkAPI.rejectWithValue(
                "Organization context is required.",
            );
        }

        if (!requestBody.name) {
            return thunkAPI.rejectWithValue("Product name is required.");
        }

        if (!requestBody.description) {
            return thunkAPI.rejectWithValue(
                "Product description is required.",
            );
        }

        try {
            const response = await api.post(
                API_ENDPOINTS.OUTREACH_PRODUCTS,
                requestBody,
                {
                    headers: {
                        "X-Organization-Id": organizationId,
                    },
                },
            );

            if (!response.data?.id) {
                return thunkAPI.rejectWithValue(
                    "Product response is missing product id.",
                );
            }

            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(error, "Failed to create product."),
            );
        }
    },
);

// Edits product name and description
export const updateProduct = createAsyncThunk(
    "products/updateProduct",
    async (productData = {}, thunkAPI) => {
        const productId = getProductId(productData);
        const requestBody = {
            product_id: productId,
        };
        let hasUpdateFields = false;

        if (!productId) {
            return thunkAPI.rejectWithValue("Invalid product id.");
        }

        if (hasOwn(productData, "name")) {
            requestBody.name = getTrimmedString(productData.name);
            hasUpdateFields = true;
        }

        if (hasOwn(productData, "description")) {
            requestBody.description = getTrimmedString(
                productData.description,
            );
            hasUpdateFields = true;
        }

        if (!hasUpdateFields) {
            return thunkAPI.rejectWithValue("No product changes to save.");
        }

        try {
            const response = await api.patch(
                API_ENDPOINTS.OUTREACH_PRODUCT_BY_ID(productId),
                requestBody,
            );

            return response.data || null;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(error, "Failed to update product."),
            );
        }
    },
);

// Get a no of campaigns/outreach sequence running on a each product
export const fetchProductSequenceCount = createAsyncThunk(
    "products/fetchProductSequenceCount",
    async (productId, thunkAPI) => {
        const normalizedProductId = getProductId(productId);

        if (!normalizedProductId) {
            return thunkAPI.rejectWithValue("Invalid product id.");
        }

        try {
            const response = await api.get(
                API_ENDPOINTS.OUTREACH_PRODUCT_SEQUENCE_COUNT(
                    normalizedProductId,
                ),
            );

            return {
                productId: normalizedProductId,
                data: response.data || null,
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(
                    error,
                    "Failed to fetch product campaign count.",
                ),
            );
        }
    },
);

export const fetchProductCampaigns = createAsyncThunk(
    "products/fetchProductCampaigns",
    async (request = {}, thunkAPI) => {
        const { productId, queryParams } = getProductCampaignRequest(request);

        if (!productId) {
            return thunkAPI.rejectWithValue("Invalid product id.");
        }

        const queryString = buildQueryString(queryParams);
        const endpoint = API_ENDPOINTS.OUTREACH_PRODUCT_SEQUENCES(productId);

        try {
            const response = await api.get(
                queryString ? `${endpoint}?${queryString}` : endpoint,
            );

            return {
                productId,
                campaigns: getSafeArray(response.data),
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(
                    error,
                    "Failed to fetch product campaigns.",
                ),
            );
        }
    },
);

const productsSlice = createSlice({
    name: "products",
    initialState: getInitialProductsState(),
    reducers: {
        setSelectedProduct: (state, action) => {
            state.selectedProduct = action.payload || null;
        },
        clearSelectedProduct: (state) => {
            state.selectedProduct = null;
        },
        clearCreateProductState: (state) => {
            state.createProductStatus = "idle";
            state.createProductError = null;
        },
        clearUpdateProductState: (state) => {
            state.updateProductStatus = "idle";
            state.updateProductError = null;
        },
        clearProductDetailsState: (state) => {
            state.productDetailsStatus = "idle";
            state.productDetailsError = null;
        },
        clearProductCampaignsForProduct: (state, action) => {
            const productId = getProductId(action.payload);

            if (!productId) return;

            delete state.productCampaignsByProductId[productId];
            delete state.productCampaignsStatusByProductId[productId];
            delete state.productCampaignsErrorByProductId[productId];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProducts.pending, (state) => {
                state.productsStatus = "loading";
                state.productsError = null;
            })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                state.productsStatus = "succeeded";
                state.productsError = null;
                state.products = action.payload;
            })
            .addCase(fetchProducts.rejected, (state, action) => {
                state.productsStatus = "failed";
                state.productsError = action.payload;
            })
            .addCase(fetchProductById.pending, (state) => {
                state.productDetailsStatus = "loading";
                state.productDetailsError = null;
            })
            .addCase(fetchProductById.fulfilled, (state, action) => {
                state.productDetailsStatus = "succeeded";
                state.productDetailsError = null;
                state.selectedProduct = action.payload;
            })
            .addCase(fetchProductById.rejected, (state, action) => {
                state.productDetailsStatus = "failed";
                state.productDetailsError = action.payload;
            })
            .addCase(createProduct.pending, (state) => {
                state.createProductStatus = "loading";
                state.createProductError = null;
            })
            .addCase(createProduct.fulfilled, (state, action) => {
                state.createProductStatus = "succeeded";
                state.createProductError = null;
                state.selectedProduct = action.payload;
            })
            .addCase(createProduct.rejected, (state, action) => {
                state.createProductStatus = "failed";
                state.createProductError = action.payload;
            })
            .addCase(updateProduct.pending, (state) => {
                state.updateProductStatus = "loading";
                state.updateProductError = null;
            })
            .addCase(updateProduct.fulfilled, (state, action) => {
                const product = action.payload;
                const productId =
                    getProductId(product) || getProductId(action.meta.arg);

                state.updateProductStatus = "succeeded";
                state.updateProductError = null;

                if (!product || !productId) return;

                const existingIndex = state.products.findIndex(
                    (item) => getProductId(item) === productId,
                );

                if (existingIndex >= 0) {
                    state.products[existingIndex] = {
                        ...state.products[existingIndex],
                        ...product,
                    };
                }

                if (getProductId(state.selectedProduct) === productId) {
                    state.selectedProduct = {
                        ...state.selectedProduct,
                        ...product,
                    };
                }
            })
            .addCase(updateProduct.rejected, (state, action) => {
                state.updateProductStatus = "failed";
                state.updateProductError = action.payload;
            })
            .addCase(fetchProductSequenceCount.pending, (state, action) => {
                const productId = getProductId(action.meta.arg);

                if (!productId) return;

                state.productSequenceCountStatusById[productId] = "loading";
                state.productSequenceCountErrorById[productId] = null;
            })
            .addCase(fetchProductSequenceCount.fulfilled, (state, action) => {
                const { productId, data } = action.payload;

                state.productSequenceCountById[productId] = data;
                state.productSequenceCountStatusById[productId] = "succeeded";
                state.productSequenceCountErrorById[productId] = null;
            })
            .addCase(fetchProductSequenceCount.rejected, (state, action) => {
                const productId = getProductId(action.meta.arg);

                if (!productId) return;

                state.productSequenceCountStatusById[productId] = "failed";
                state.productSequenceCountErrorById[productId] =
                    action.payload;
            })
            .addCase(fetchProductCampaigns.pending, (state, action) => {
                const { productId } = getProductCampaignRequest(
                    action.meta.arg,
                );

                if (!productId) return;

                state.productCampaignsStatusByProductId[productId] =
                    "loading";
                state.productCampaignsErrorByProductId[productId] = null;
            })
            .addCase(fetchProductCampaigns.fulfilled, (state, action) => {
                const { productId, campaigns } = action.payload;

                state.productCampaignsByProductId[productId] = campaigns;
                state.productCampaignsStatusByProductId[productId] =
                    "succeeded";
                state.productCampaignsErrorByProductId[productId] = null;
            })
            .addCase(fetchProductCampaigns.rejected, (state, action) => {
                const { productId } = getProductCampaignRequest(
                    action.meta.arg,
                );

                if (!productId) return;

                state.productCampaignsStatusByProductId[productId] = "failed";
                state.productCampaignsErrorByProductId[productId] =
                    action.payload;
            })
            .addCase(logout, () => getInitialProductsState());
    },
});

export const {
    setSelectedProduct,
    clearSelectedProduct,
    clearCreateProductState,
    clearUpdateProductState,
    clearProductDetailsState,
    clearProductCampaignsForProduct,
} = productsSlice.actions;

export const selectProductsState = (state) => state.products;
export const selectProducts = (state) => state.products.products;
export const selectSelectedProduct = (state) => state.products.selectedProduct;

export default productsSlice.reducer;
